/**
 * Production-records fill + sign REST client (Complete service wizard).
 *
 * These endpoints live under `/api/production-records/*` (the PR domain)
 * but are driven by the cafeteria Complete-service flow. Online-only —
 * signing is a compliance action that must hit the server.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Mealtime } from './cafeteria'
import type { TemperatureReadingWire } from './cafeteria-temperatures'
import { type ApiError, apiFetch } from './http'

// ---------------------------------------------------------------------------
// Wire shapes (mirror apps/ordo/server/src/managers/cafeteria.ts)
// ---------------------------------------------------------------------------

/** One age-group sub-row beneath a production-fill deliverable. */
export interface ProductionFillGradeRow {
  gradeGroupId: string | null
  gradeGroupName: string | null
  /** Homeroom/group display name — non-AGE_GROUP dimensions (gradeGroupName is null there). */
  rosterName: string | null
  /** Stored group tuple + display label. */
  groupDimension: string | null
  groupValue: string | null
  groupLabel: string | null
  tierGradeRangeId: string | null
  planned: number
  served: number
  reimbursable: number
  nonReimbursable: number
  leftover: number
  /**
   * PROD-1279 unified input — the school's stored Ordered / Prepared / Received
   * for this band, null when never entered (the input then prefills from the
   * order / delivery plan instead). The server ALWAYS sends the three keys
   * (plan-05 §3.2); they are optional on this mirror only so fixtures written
   * before they existed keep compiling. Read them as `row.ordered ?? null`.
   */
  ordered?: number | null
  prepared?: number | null
  received?: number | null
  /**
   * PROD-1511 — true when nothing was tallied or saved for this band and the
   * server pre-filled `served` with every meal ordered (the fully-served
   * default). The review labels such a Served "Full order". Absent from an
   * older server: read as `row.servedDefaulted === true`.
   */
  servedDefaulted?: boolean
  /**
   * Milk ordered on the Field Trip menu for this band, kept out of `planned`
   * so the record shows it on the milk row's field-trip line. Absent from an
   * older server (and on food): read as 0.
   */
  fieldTripPlanned?: number
  /**
   * Milk cartons the school sent out with field-trip meals. Lowers Leftover,
   * never part of Served. Absent from an older server: read as 0.
   */
  fieldTrip?: number
}

/** A production-record line × tier the deliverable's edits distribute into. */
export interface ProductionFillLineTarget {
  lineId: string | null
  bundleDeliverableId: string | null
  menuItemId: string | null
  tierGradeRangeId: string | null
  planned: number
  /** The line's item is on the Field Trip menu (milk only). Absent from an older server. */
  isFieldTrip?: boolean
}

export interface ProductionFillDeliverable {
  deliverableKey: string
  label: string
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null
  isBulk: boolean
  portionLabel: string | null
  gradeRows: ProductionFillGradeRow[]
  lineTargets: ProductionFillLineTarget[]
  serviceTemperature: ProductionFillServiceTemp
  /**
   * The one component the package is (an à-la-carte item, or a single-pick
   * package), else null. Joins a weekly milk SKU to the milk it was ordered
   * as. Optional: an older server omits it.
   */
  componentId?: string | null
  /**
   * A standalone milk SKU: its Received box is what the meal started with,
   * drop or no drop (Amana, 2026-11-03). Optional: an older server omits it,
   * and then only a weekly-milk row counts that way.
   */
  isMilk?: boolean
  /**
   * A milk deliverable's carry from an earlier SERVICE DAY (milk is one shelf
   * for the week; earlier meals of the same day are the client's to read).
   * Null when no earlier meal counted this milk; absent on food. Optional: an
   * older server omits it, and a missing field means today's behaviour (the
   * row defaults to the delivery's count).
   */
  carryIn?: ProductionFillCarryIn | null
  /** A meal sold only on the Field Trip menu: the input files the trip's milk under it. Optional: older servers omit it. */
  fieldTripMeal?: boolean
}

/** A milk deliverable's carry from an earlier service day (milk is one shelf for the week). */
export interface ProductionFillCarryIn {
  /** The SAME milk's (same component) Leftover at the latest earlier meal at this campus that counted it, summed over that record's bands. */
  value: number
  /** yyyy-mm-dd of that earlier meal. */
  fromServiceDate: string
  fromMealtime: Mealtime
  /** That milk on trucks delivered after `fromServiceDate`, up to and including this service day, one entry per delivery date (ascending), quantity = Σ receivedQuantity ?? plannedQuantity over non-voided lines. */
  receivedSince: Array<{ deliveryDate: string; quantity: number }>
}

export interface ProductionFillServiceTemp {
  /** Committed SERVICE reading (°F); null until the worker records it. */
  value: number | null
  /** Chef's kitchen/delivery reading for this package, the suggested default. */
  prefill: number | null
  /** The cafeteria's own service-line reading (Temperature logs page); pre-fills ahead of the chef's. */
  cafeteria: TemperatureReadingWire | null
  status: string | null
}

export type ProductionWriteInKind = 'LEFTOVER' | 'BACKUP' | 'DROPPED'

/**
 * One footer write-in on a production record — meals served or lost outside
 * the shipment-derived lines (re-served leftovers, backup meals, dropped
 * meals).
 */
export interface ProductionFillWriteIn {
  id: string
  kind: ProductionWriteInKind
  /** LEFTOVER only: the recent menu item the carryover came from. */
  menuItemId: string | null
  menuItemName: string | null
  description: string | null
  quantity: number
  reimbursableQty: number
  nonReimbursableQty: number
}

export interface ProductionFillMealtime {
  productionRecordId: string
  mealtime: Mealtime
  status: string
  deliverables: ProductionFillDeliverable[]
  writeIns: ProductionFillWriteIn[]
  /**
   * PROD-1279 — the "Time reading" already stored against this record's SERVICE
   * rows (ISO), or null when none was recorded. The Service-records flow seeds
   * its Time reading input from this when reopening or amending, so a signed
   * record's HACCP time is shown back rather than re-entered.
   *
   * REQUIRED, not optional: the server always sends the key, and a fixture that
   * forgets it is a test mounting a record shape the API cannot produce.
   */
  serviceReadingAt: string | null
}

export interface ProductionFillResponse {
  serviceDate: string
  signed: boolean
  mealtimes: ProductionFillMealtime[]
}

/**
 * The amendment reason travels only when the flow is amending a signed
 * record. The complete flow's request bodies are unchanged — a `reason: null`
 * key would be noise on every first signature and a moving target for the
 * body-pinning tests.
 */
function amendmentReasonBody(reason: string | null | undefined): { reason?: string } {
  const trimmed = reason?.trim() ?? ''
  return trimmed === '' ? {} : { reason: trimmed }
}

export interface SaveQuantitiesInput {
  productionRecordId: string
  /** Why a signed record is being changed — lands on its amendment trail. */
  reason?: string | null
  lines: Array<{
    lineId?: string | null
    bundleDeliverableId?: string | null
    menuItemId?: string | null
    buckets: Array<{
      /** Portion tier (the PR bucket tier key). */
      gradeRangeId?: string | null
      /** `(groupDimension, groupValue)` — the PR bucket identity (both or neither). */
      groupDimension?: string | null
      groupValue?: string | null
      servedQty: number
      reimbursableQty: number
      nonReimbursableQty: number
      leftoverQty: number
      /**
       * PROD-1279 unified input — the school's own Ordered / Prepared /
       * Received for this bucket. Absent = not entered: the stored column is
       * left untouched. Present = written and marked entered, so the record
       * document prefers it over the shipment-derived figure (plan-05 §3.1).
       */
      orderedQty?: number | null
      preparedQty?: number | null
      receivedQty?: number | null
      /** Milk sent out with field-trip meals. Absent = the stored count is left untouched. */
      fieldTripQty?: number | null
    }>
  }>
}

/** The HACCP checkpoint a record-scoped temperature save belongs to (plan-05 §3.3). */
export type RecordTempCheckpoint = 'FACILITY' | 'DELIVERY' | 'SERVICE'

export interface SaveServiceTempsInput {
  productionRecordId: string
  temperatures: Array<{ bundleDeliverableId: string | null; menuItemId: string | null; value: number }>
  /**
   * When the school says these readings were taken — the "Time reading" input
   * on the Service records task, combined with the service date (PROD-1279
   * D13). Stamped on every SERVICE row the save creates and printed as the
   * record's "Logged" time. Omitted when the worker leaves the box empty.
   */
  readingAt?: string | null
  /**
   * Which checkpoint the rows are (PROD-1279 unified input). Omitted = SERVICE,
   * exactly what every deployed caller sends today. FACILITY / DELIVERY rows are
   * RECORD-scoped (no source event): the Kitchen °F / Received °F a school
   * enters for an item that has no truck line on the record. Never a
   * `ShipmentEvent`.
   */
  checkpoint?: RecordTempCheckpoint
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

export async function fetchProductionFill(
  args: { schoolLocationId: string; serviceDate: string },
  signal?: AbortSignal,
): Promise<ProductionFillResponse> {
  return apiFetch<ProductionFillResponse>('/api/production-records/draft', { query: args, signal })
}

export async function saveProductionQuantities(input: SaveQuantitiesInput): Promise<{ saved: boolean }> {
  return apiFetch(`/api/production-records/${input.productionRecordId}/quantities`, {
    method: 'POST',
    body: { lines: input.lines, ...amendmentReasonBody(input.reason) },
  })
}

export async function saveServiceTemperatures(input: SaveServiceTempsInput): Promise<{ saved: number }> {
  return apiFetch(`/api/production-records/${input.productionRecordId}/service-temps`, {
    method: 'POST',
    // Conditional spreads, not `readingAt: null` / `checkpoint: 'SERVICE'`: with
    // no time and no checkpoint the body stays byte-identical to what every
    // deployed client sends today.
    body: {
      temperatures: input.temperatures,
      ...(input.readingAt ? { readingAt: input.readingAt } : {}),
      ...(input.checkpoint ? { checkpoint: input.checkpoint } : {}),
    },
  })
}

export interface SaveWriteInsInput {
  productionRecordId: string
  /** Why a signed record is being changed — lands on its amendment trail. */
  reason?: string | null
  /** The full desired set — rows the worker removed are soft-deleted server-side. */
  writeIns: Array<{
    id?: string | null
    kind: ProductionWriteInKind
    menuItemId?: string | null
    description?: string | null
    quantity: number
    reimbursableQty: number
    nonReimbursableQty: number
  }>
}

export async function saveProductionWriteIns(input: SaveWriteInsInput): Promise<{ saved: boolean }> {
  return apiFetch(`/api/production-records/${input.productionRecordId}/write-ins`, {
    method: 'POST',
    body: { writeIns: input.writeIns, ...amendmentReasonBody(input.reason) },
  })
}

/** A menu item recently on this school's production records (LEFTOVER pick list). */
export interface RecentServiceMenuItem {
  menuItemId: string
  name: string
  /** yyyy-mm-dd of the most recent service the item appeared on. */
  lastServiceDate: string
}

export async function fetchRecentServiceMenuItems(
  args: { schoolLocationId: string; serviceDate: string },
  signal?: AbortSignal,
): Promise<{ items: RecentServiceMenuItem[] }> {
  return apiFetch<{ items: RecentServiceMenuItem[] }>('/api/production-records/recent-menu-items', {
    query: args,
    signal,
  })
}

export interface SignProductionRecordInput {
  productionRecordId: string
  signedByName?: string | null
  /** Why an already-signed record is being re-signed — lands on its amendment trail. */
  reason?: string | null
}

export async function signProductionRecord(
  input: SignProductionRecordInput,
): Promise<{ signed: boolean; status: string }> {
  return apiFetch(`/api/production-records/${input.productionRecordId}/sign`, {
    method: 'POST',
    body: { signedByName: input.signedByName ?? null, ...amendmentReasonBody(input.reason) },
  })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const productionFillKey = (args: { schoolLocationId: string; serviceDate: string }) =>
  ['cafeteria', 'production-fill', args] as const

export function useProductionFillQuery(args: { schoolLocationId: string; serviceDate: string } | null) {
  return useQuery<ProductionFillResponse>({
    enabled: Boolean(args),
    queryKey: productionFillKey(args ?? { schoolLocationId: '', serviceDate: '' }),
    queryFn: ({ signal }) => fetchProductionFill(args as { schoolLocationId: string; serviceDate: string }, signal),
    // Day chevrons re-key this query; keep the previous day rendered so the
    // page chrome survives the step. See api/cafeteria.ts "Hooks" note.
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000,
  })
}

export function useSaveProductionQuantitiesMutation() {
  const qc = useQueryClient()
  return useMutation<{ saved: boolean }, ApiError, SaveQuantitiesInput>({
    mutationFn: saveProductionQuantities,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cafeteria', 'production-fill'] }),
  })
}

export function useSaveServiceTemperaturesMutation() {
  const qc = useQueryClient()
  return useMutation<{ saved: number }, ApiError, SaveServiceTempsInput>({
    mutationFn: saveServiceTemperatures,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cafeteria', 'production-fill'] }),
  })
}

export function useSaveProductionWriteInsMutation() {
  const qc = useQueryClient()
  return useMutation<{ saved: boolean }, ApiError, SaveWriteInsInput>({
    mutationFn: saveProductionWriteIns,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cafeteria', 'production-fill'] }),
  })
}

/** The LEFTOVER pick list: menu items on this school's records in the prior two weeks. */
export function useRecentServiceMenuItemsQuery(args: { schoolLocationId: string; serviceDate: string } | null) {
  return useQuery<{ items: RecentServiceMenuItem[] }>({
    enabled: Boolean(args),
    queryKey: ['cafeteria', 'recent-menu-items', args],
    queryFn: ({ signal }) =>
      fetchRecentServiceMenuItems(args as { schoolLocationId: string; serviceDate: string }, signal),
    staleTime: 60 * 1000,
  })
}

export function useSignProductionRecordMutation() {
  const qc = useQueryClient()
  return useMutation<{ signed: boolean; status: string }, ApiError, SignProductionRecordInput>({
    mutationFn: signProductionRecord,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'production-fill'] })
      // Signing closes the day's complete-service task — refresh the
      // outstanding count + history rows.
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'outstanding-days'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-history'] })
      // Signing can flip the school week view's History badge and changes
      // the served basis on the service-details page (PROD-1123 v2).
      void qc.invalidateQueries({ queryKey: ['school-orders', 'service-summary'] })
      void qc.invalidateQueries({ queryKey: ['school-orders', 'service-day'] })
    },
  })
}
